import './style/StoreHeader.css';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import searchIcon from '../../media/search.png';
import Icon from '../block/Icon';
import Input from '../block/Input';
import Divider from '../block/Divider';
import { getImgUrl } from '../../util/ImageSource';
import Image from '../block/Image';
import { formatToCurrency, displayPriceForProduct, availableQuantityForProduct, quantityTypeForProduct, capitalizeFirstLetter, displayLotSizeForProduct } from '../../util/Calculate';
import { getProducts, emptySearchResults } from '../../redux/temporarySlice';
import SimpleLoader from '../block/SimpleLoader';
import WordmarkWhite from '../../media/materialize-logo-wordmark-white.png';
import { Navigate, Text } from '@harperai/materialize-design-system';
import useResponsiveBreakpoint from '../../hooks/ResponseBreakpoint';

const StoreHeader = ({ searchInputValue, setSearchInputValue, cart, showCart }) => {
    const containerRef = useRef(null);
    const isMobile = useResponsiveBreakpoint(containerRef, 767, 'width');

    const { pathname, search } = useLocation();

    const queryString = useMemo(() => {
        const searchParams = new URLSearchParams(search);
        return searchParams.get('query');
    }, [search]);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [resultsVisible, setResultsVisible] = useState(false);
    const inputRef = useRef(null);
    const desktopResultsWrapperRef = useRef(null);
    const resultsWrapperRef = useRef(null);

    const searching = useSelector((state) => state.temporary?.gettingProducts);
    const searchResults = useSelector((state) => state.temporary?.searchResults);

    const handleSearchInputChange = (e) => {
        setSearchInputValue(e.target.value);
    };

    useEffect(() => {
        if (searchInputValue?.length > 0) {
            setResultsVisible(true);
        }
    }, [searchInputValue]);

    useEffect(() => {
        const debounceTimeout = setTimeout(() => {;
            if (searchInputValue?.length > 0) {
                dispatch(getProducts({
                    query: encodeURIComponent(searchInputValue),
                    category: "",
                    manufacturer: "",
                    page: 0,
                    limit: 5
                }));
            } else {
                dispatch(emptySearchResults());
            }
        }, 500);
        return () => {
            clearTimeout(debounceTimeout);
        };
    }, [dispatch, searchInputValue]);

    const handleEnterKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSearch = () => {
        setResultsVisible(false);
        setSearchInputValue('');
        navigate(`/search?query=${searchInputValue}`);
    };

    const handleInputFocus = () => {
        console.log('Input focused');
        setResultsVisible(true);
    };

    // Listen for clicks outside the component
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                resultsWrapperRef.current &&
                !resultsWrapperRef.current.contains(event.target) &&
                desktopResultsWrapperRef.current &&
                !desktopResultsWrapperRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setResultsVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const QueryHighlight = ({ string = "" }) => {
        let prefix = "";
        let matching = "";
        let suffix = "";

        let startIndex = string.toLowerCase().indexOf(searchInputValue.toLowerCase());
        let endIndex = startIndex + searchInputValue.length;
        if (startIndex > -1) {
            matching = string.substring(startIndex, endIndex);
            prefix = string.substring(0, startIndex);
            suffix = string.substring(endIndex);
        } else {
            prefix = string;
        }

        return (
            <>{prefix}<span className="highlight-text">{matching}</span>{suffix}</>
        );
    }

    const searchTemplate = (classes) => {
        return (
            <div id="product-search-container" className="relative flex horizontal gap-m" style={{flexGrow: 1}}>
                <div id="product-search-wrapper" className={`${isMobile ? "" : "relative"} ${searchResults?.length > 0 ? "results" : ""} ${classes}`}>
                    <Icon customId="product-search-icon" icon={searchIcon} color="white" classes={searchInputValue?.length > 0 ? "has-value" : ""} size="24" />
                    <Input customId="product-search-input" classes={searchInputValue?.length > 0 ? "has-value" : ""} inputRef={inputRef} onKeyDown={handleEnterKeyPress} onChange={handleSearchInputChange} value={searchInputValue} placeholder="Search" flex={true} onBrand={true} onFocus={handleInputFocus} />
                    {(resultsVisible && searchInputValue?.length > 0) && (
                        <div id="product-search-results-wrapper" ref={classes === "mobile-hide" ? desktopResultsWrapperRef : resultsWrapperRef} className="flex vertical relative">
                            {(searchInputValue?.length > 0 && searchResults?.length > 0) && (
                                <div className="flex vertical">
                                    <div className="flex horizontal align-center pad-xs gap-s">
                                        <Navigate href={`/search?query=${searchInputValue}`} variant="transparent" flexGrow={1}>
                                            {`View all products matching '${searchInputValue}'`}
                                        </Navigate>
                                    </div>
                                    <Divider vertical={false} />
                                </div>
                            )}
                            {searchResults?.length > 0 ? searchResults?.map((product, index) => (
                                <Navigate href={`/products/${encodeURIComponent(product.marketManufacturer?.toLowerCase())}/${encodeURIComponent(product.marketMpn?.toLowerCase())}`} classes="result-row" variant="transparent" unsetHeight={true}>
                                    <div className="flex horizontal flex-1 height-100 pad-xs align-center no-pointer">
                                        <Image classes="product-image" source={getImgUrl(product)} altTag={product.marketMpn} width="small" />
                                        <div className="flex horizontal flex-1 space-between">
                                            <div className="flex vertical pad-m">
                                                <Text size="caption" variant="secondary" bold={true}>
                                                    <QueryHighlight string={product.marketManufacturer} />
                                                </Text>
                                                <Text size="paragraph" bold={true}>
                                                    <QueryHighlight string={product.marketMpn} />
                                                </Text>
                                            </div>
                                            <div className="flex vertical pad-m align-end">
                                                <Text size="caption" variant="secondary" bold={true}>{`${availableQuantityForProduct(product)} ${capitalizeFirstLetter(quantityTypeForProduct(product))} ${displayLotSizeForProduct(product)}`}</Text>
                                                <Text size="paragraph" bold={true}>{displayPriceForProduct(product) === 0 ? "N/A" : formatToCurrency(displayPriceForProduct(product))}</Text>
                                            </div>
                                        </div>
                                    </div>
                                </Navigate>
                            )) : !searching && (
                                <div className="flex vertical align-center pad-s gap-s">
                                    <Text size="caption" variant="secondary" classes="center-text" bold={true}>{`No products found matching "${searchInputValue}"`}</Text>
                                </div>
                            )}
                            <SimpleLoader loading={searching} classes={`${searching ? "fade-in" : ""}`} />
                        </div>
                    )}
                    
                </div>
                <Navigate href={searchInputValue?.length === 0 ? null : `/search?query=${searchInputValue}`} variant="yellow" size="large" classes={classes}>Go</Navigate>
            </div>
        )
    }

    return (
        <div ref={containerRef} className="shop-max-width flex vertical pad-m no-pad-top no-pad-bot width-100">
            <div className="flex flex-wrap horizontal space-between pad-m gap-xl black-bg">
                <div className="flex horizontal">
                    <Navigate href="https://www.trymaterialize.com/" newTab={true} size="small" variant="transparent" darkMode={true}>
                        How We Work
                    </Navigate>
                    {!isMobile && <Navigate href="https://www.trymaterialize.com/terms-of-use" newTab={true} size="small" variant="transparent" darkMode={true}>
                        Terms of Use
                    </Navigate>}
                    {!isMobile && <Navigate href="https://www.trymaterialize.com/privacy-policy" newTab={true} size="small" variant="transparent" darkMode={true}>
                        Privacy Policy
                    </Navigate>}
                </div>
                <Navigate href="mailto:orders@trymaterialize.com" newTab={true} size="small" variant="transparent" darkMode={true}>orders@trymaterialize.com</Navigate>
            </div>
            <div id="store-header" className="flex horizontal width-100 space-between align-center pad-s gap-m">
                <div className="vertical pad-m no-pad-bot no-pad-top no-pad-right flex height-100 relative justify-center">
                    <Navigate href="/" className="flex">
                        <img src={WordmarkWhite} alt="Materialize Wordmark" className="wordmark-image" />
                    </Navigate>
                </div>
                {searchTemplate("mobile-hide")}
                <div className="vertical flex height-100 pad-xs relative justify-center">
                    <Navigate href="/cart" variant="secondary" icon="cart" size="large" darkMode={true} active={pathname === "/cart"}>
                        Cart
                    </Navigate>
                    {cart?.length > 0 && (
                        <div id="cart-count" className="flex align-center justify-center">
                            <Text size="caption" bold={true}>{cart.length}</Text>
                        </div>
                    )}
                </div>
            </div>
            <div id="store-header-footer" className="flex horizontal space-between pad-s no-pad-top gap-xl black-bg desktop-hide">
                {searchTemplate()}
            </div>
        </div>
    );
};

export default StoreHeader;